import React from 'react';
import styled, { css } from 'styled-components';

const HeroSectionWrapper = styled.section`
    width: 100%;
    background: #f5f8f2;
    overflow: hidden;
`;

const HeroTop = styled.div`
    position: relative;
    width: 100%;
`;

const HeroBackground = styled.div`
    position: relative;
    width: 100%;
    min-height: 599px;
    background-image: ${({ desktopImage }) => (desktopImage ? `url(${desktopImage})` : 'none')};
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    overflow: hidden;

    @media (max-width: 1024px) {
        min-height: 500px;
    }

    @media (max-width: 768px) {
        min-height: 420px;
        background-image: ${({ mobileImage, desktopImage }) =>
            mobileImage ? `url(${mobileImage})` : desktopImage ? `url(${desktopImage})` : 'none'};
        background-position: center center;
    }

    @media (max-width: 480px) {
        min-height: 380px;
    }
`;

const HeroInner = styled.div`
    position: relative;
    z-index: 3;
    max-width: 1440px;
    min-height: 599px;
    margin: 0 auto;
    padding: 88px 80px 120px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 40px;

    @media (max-width: 1280px) {
        padding: 72px 56px 110px;
    }

    @media (max-width: 1024px) {
        min-height: 500px;
        padding: 64px 40px 100px;
    }

    @media (max-width: 768px) {
        min-height: 420px;
        padding: 40px 24px 70px;
        flex-direction: column;
        justify-content: flex-start;
        gap: 24px;
    }

    @media (max-width: 480px) {
        min-height: 380px;
        padding: 28px 16px 56px;
        gap: 16px;
    }
`;

const HeroTitle = styled.h1`
    max-width: 640px;
    margin: 60px 0 0;
    color: #fff;
    font-family: 'Montserrat', sans-serif;
    font-size: 48px;
    font-style: normal;
    font-weight: 500;
    line-height: 58px;

    @media (max-width: 1280px) {
        max-width: 560px;
        font-size: 42px;
        line-height: 52px;
    }

    @media (max-width: 1024px) {
        max-width: 520px;
        font-size: 38px;
        line-height: 48px;
    }

    @media (max-width: 768px) {
        max-width: 100%;
        margin-top: 24px;
        text-align: center;
        font-size: 34px;
        line-height: 42px;
    }

    @media (max-width: 480px) {
        font-size: 28px;
        line-height: 38px;
        letter-spacing: -0.3px;
    }
`;

const SealGroup = styled.div`
    position: relative;
    min-width: 320px;
    height: 240px;
    margin-top: 105px;

    @media (max-width: 1024px) {
        min-width: 260px;
        height: 210px;
        margin-top: 100px;
    }

    @media (max-width: 768px) {
        align-self: flex-end;
        width: 170px;
        min-width: 170px;
        height: 110px;
        margin-top: auto;
        margin-right: 6px;
    }

    @media (max-width: 480px) {
        width: 148px;
        min-width: 148px;
        height: 96px;
        margin-right: 0;
    }
`;

const sealLargeStyles = css`
    width: 300px;
    height: 300px;
    right: 85px;
    bottom: 30px;

    @media (max-width: 1024px) {
        width: 132px;
        height: 132px;
        right: 56px;
        top: 36px;
    }

    @media (max-width: 768px) {
        width: 92px;
        height: 92px;
        right: 36px;
        top: 4px;
    }

    @media (max-width: 480px) {
        width: 86px;
        height: 86px;
        right: 34px;
        top: 4px;
    }
`;

const sealSmallStyles = css`
    width: 112px;
    height: 112px;
    right: 0;
    top: 54px;

    @media (max-width: 1024px) {
        width: 98px;
        height: 98px;
        top: 56px;
    }

    @media (max-width: 768px) {
        width: 68px;
        height: 68px;
        right: -2px;
        top: 18px;
    }

    @media (max-width: 480px) {
        width: 62px;
        height: 62px;
        right: -4px;
        top: 18px;
    }
`;

const SealCircle = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    ${({ large }) =>
        large &&
        css`
            border-radius: 50%;
            background: #fff;
            border: 4px solid #d5e3c7;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);

            @media (max-width: 768px) {
                border-width: 3px;
            }
        `}

    ${({ large: isLarge }) => isLarge && sealLargeStyles}
    ${({ small }) => small && sealSmallStyles}
`;

const SealImage = styled.img`
    width: ${({ small }) => (small ? '100%' : '72%')};
    height: ${({ small }) => (small ? '100%' : '72%')};
    object-fit: contain;
`;

const ContentSection = styled.div`
    width: 100%;
    margin-top: -6px;
    padding: 28px 0 56px;
    background: #f5f8f2;

    @media (max-width: 768px) {
        padding: 16px 0 32px;
    }
`;

const ContentInner = styled.div`
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 80px;
    display: grid;
    grid-template-columns: 522px minmax(0, 1fr);
    column-gap: 126px;
    align-items: start;
    margin-top: 30px;

    @media (max-width: 1280px) {
        padding: 0 56px;
        grid-template-columns: minmax(420px, 522px) minmax(0, 1fr);
        column-gap: 72px;
        margin-top: 25px;
    }

    @media (max-width: 1024px) {
        padding: 0 40px;
        grid-template-columns: 1fr;
        row-gap: 32px;
        margin-top: 22px;
    }

    @media (max-width: 768px) {
        padding: 0 24px;
        row-gap: 24px;
        margin-top: 18px;
    }

    @media (max-width: 480px) {
        padding: 0 16px;
        margin-top: 12px;
    }
`;

const LeftColumn = styled.div`
    display: flex;
    width: 522px;
    max-width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 40px;
    flex-shrink: 0;

    @media (max-width: 1024px) {
        width: 100%;
        gap: 28px;
    }

    @media (max-width: 768px) {
        gap: 22px;
    }
`;

const StatusContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    align-self: stretch;
`;

const SectionLabel = styled.p`
    margin: 0;
    color: #6ea44c;
    font-family: 'Montserrat', sans-serif;
    font-size: 19.2px;
    font-style: normal;
    font-weight: 400;
    line-height: 180%;
    letter-spacing: 3.84px;
    text-transform: uppercase;

    @media (max-width: 768px) {
        font-size: 14px;
        letter-spacing: 2.8px;
    }
`;

const StatusTitle = styled.h2`
    margin: 0;
    color: #004d37;
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 42px;

    @media (max-width: 768px) {
        font-size: 22px;
        line-height: 32px;
    }
`;
const StatusParagraph = styled.p`
    margin: 0;
    max-width: 522px;
    color: #003626;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 180%;

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const BulletList = styled.ul`
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const BulletItem = styled.li`
    color: #003626;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 180%;

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const StatusDot = styled.span`
    width: 10px;
    height: 10px;
    min-width: 10px;
    border-radius: 50%;
    background: #6ea44c;
    box-shadow: 0 0 0 5px rgba(110, 164, 76, 0.16);
`;

const RightColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
`;

const StatsGrid = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(220px, 1fr));
    gap: 12px;

    @media (max-width: 1280px) {
        grid-template-columns: repeat(2, minmax(200px, 1fr));
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const StatCard = styled.div`
    display: flex;
    min-height: 108px;
    padding: 32px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    border-radius: 20px;
    background: #fff;
    box-shadow: 2px 4px 15px 0 rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
        min-height: 102px;
        padding: 24px 20px;
        border-radius: 16px;
    }
`;

const StatValue = styled.h3`
    margin: 0;
    color: #004d37;
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 42px;

    @media (max-width: 768px) {
        font-size: 20px;
        line-height: 28px;
    }
`;

const StatLabel = styled.p`
    margin: 0;
    max-width: 240px;
    color: #6d716f;
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
`;

const UpdatedText = styled.p`
    width: 100%;
    margin: 0;
    color: #6d716f;
    text-align: right;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-style: italic;
    font-weight: 400;
    line-height: 150%;

    @media (max-width: 768px) {
        font-size: 12px;
    }
`;
const HeroSection = ({
    partnerName,
    heroTitle,
    statusTitle,
    bulletPoints = [],
    stats = [],
    images,
}) => {
    return (
        <HeroSectionWrapper>
            <HeroTop>
                <HeroBackground
                    desktopImage={images && images.desktopHero}
                    mobileImage={images && images.mobileHero}
                >
                    <HeroInner>
                        <HeroTitle>
                            {heroTitle && heroTitle.replaceAll('[Partner name]', partnerName)}
                        </HeroTitle>

                        <SealGroup>
                            <SealCircle large>
                                <SealImage src={images && images.partnerSeal} alt="Partner seal" />
                            </SealCircle>

                            <SealCircle small>
                                <SealImage
                                    small
                                    src={images && images.regeneraSeal}
                                    alt="Regenera seal"
                                />
                            </SealCircle>
                        </SealGroup>
                    </HeroInner>
                </HeroBackground>
            </HeroTop>

            <ContentSection>
                <ContentInner>
                    <LeftColumn>
                        <StatusContent>
                            <SectionLabel>STATUS</SectionLabel>

                            {/* <StatusTitle>
                                {statusTitle &&
                                    statusTitle.replaceAll('[Partner name]', partnerName)}
                            </StatusTitle> */}
                            <StatusTitle>
                                {(bulletPoints[0] || statusTitle || '').replaceAll(
                                    '[Partner name]',
                                    partnerName
                                )}
                            </StatusTitle>

                            {bulletPoints[1] && (
                                <StatusParagraph>
                                    {bulletPoints[1].replaceAll('[Partner name]', partnerName)}
                                </StatusParagraph>
                            )}
                        </StatusContent>
                    </LeftColumn>

                    <RightColumn>
                        <StatsGrid>
                            {stats.map(item => (
                                <StatCard key={item.id}>
                                    <StatValue>{item.value}</StatValue>
                                    <StatLabel>{item.label}</StatLabel>
                                </StatCard>
                            ))}
                        </StatsGrid>
                    </RightColumn>
                </ContentInner>
            </ContentSection>
        </HeroSectionWrapper>
    );
};

export default HeroSection;
